import React from "react";
import { api } from "../../lib/api";
import { useSite } from "../../lib/site-context";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";

export default function SettingsAdmin() {
  const { refresh } = useSite();
  const [s, setS] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { api.adminSettings().then(setS); }, []);
  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.updateSettings(s);
      setS(r);
      refresh();
      toast.success("Einstellungen gespeichert");
    } finally { setBusy(false); }
  };
  const uploadLogo = async (e, key) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = await api.uploadMedia(f, "image");
    setS({ ...s, [key]: r.id, [key.replace("_id", "_filename")]: r.filename });
    toast.success("Logo hochgeladen – nicht vergessen zu speichern.");
  };

  const setTheme = (k, v) => setS({ ...s, theme: { ...s.theme, [k]: v } });
  const setContact = (k, v) => setS({ ...s, contact: { ...s.contact, [k]: v } });

  const Field = ({ label, value, onChange, type = "text" }) => (
    <div>
      <label className="brut-label">{label}</label>
      <input type={type} className="brut-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );

  return (
    <div data-testid="admin-settings">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div><div className="brut-tag mb-3">CMS / EINSTELLUNGEN</div><h1 className="brut-h2">Einstellungen</h1></div>
        <button onClick={save} disabled={busy} className="brut-btn !py-2 !px-3 text-xs"><Save size={12}/> Speichern</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <section className="border-2 border-[var(--ink)] p-5 space-y-4">
          <h2 className="brut-h3">Branding & Theme</h2>
          <Field label="Website-Name" value={s.site_name} onChange={(v) => setS({ ...s, site_name: v })} />
          <Field label="Tagline (DE)" value={s.tagline_de} onChange={(v) => setS({ ...s, tagline_de: v })} />
          <Field label="Tagline (EN)" value={s.tagline_en} onChange={(v) => setS({ ...s, tagline_en: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Akzentfarbe" type="color" value={s.theme.accent_color} onChange={(v) => setTheme("accent_color", v)} />
            <Field label="Akzent-Kontrasttext" type="color" value={s.theme.accent_contrast} onChange={(v) => setTheme("accent_contrast", v)} />
            <Field label="Hintergrund" type="color" value={s.theme.bg_color} onChange={(v) => setTheme("bg_color", v)} />
            <Field label="Textfarbe" type="color" value={s.theme.text_color} onChange={(v) => setTheme("text_color", v)} />
            <Field label="Muted-Textfarbe" type="color" value={s.theme.text_muted} onChange={(v) => setTheme("text_muted", v)} />
            <Field label="Rahmenfarbe" type="color" value={s.theme.border_color} onChange={(v) => setTheme("border_color", v)} />
            <Field label="Eckenradius (px)" type="number" value={s.theme.radius} onChange={(v) => setTheme("radius", v)} />
            <Field label="Schriftfamilie" value={s.theme.font_family} onChange={(v) => setTheme("font_family", v)} />
          </div>
          <div>
            <label className="brut-label">Logo</label>
            <div className="flex items-center gap-3">
              <label className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs cursor-pointer">
                <Upload size={12}/> Logo hochladen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e, "logo_media_id")} />
              </label>
              {s.logo_media_id && <span className="font-mono text-xs opacity-70 truncate">{s.logo_media_id}</span>}
            </div>
          </div>
        </section>

        <section className="border-2 border-[var(--ink)] p-5 space-y-4">
          <h2 className="brut-h3">Kontakt & Impressum-Daten</h2>
          <Field label="Firma" value={s.contact.company} onChange={(v) => setContact("company", v)} />
          <Field label="Straße" value={s.contact.street} onChange={(v) => setContact("street", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="PLZ" value={s.contact.zip} onChange={(v) => setContact("zip", v)} />
            <Field label="Ort" value={s.contact.city} onChange={(v) => setContact("city", v)} />
          </div>
          <Field label="Land" value={s.contact.country} onChange={(v) => setContact("country", v)} />
          <Field label="Telefon" value={s.contact.phone} onChange={(v) => setContact("phone", v)} />
          <Field label="Mobil" value={s.contact.mobile} onChange={(v) => setContact("mobile", v)} />
          <Field label="WhatsApp" value={s.contact.whatsapp} onChange={(v) => setContact("whatsapp", v)} />
          <Field label="E-Mail" value={s.contact.email} onChange={(v) => setContact("email", v)} />
          <Field label="Rechtsname" value={s.contact.legal_name} onChange={(v) => setContact("legal_name", v)} />
          <Field label="Geschäftsführer" value={s.contact.ceo} onChange={(v) => setContact("ceo", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Registergericht" value={s.contact.register_court} onChange={(v) => setContact("register_court", v)} />
            <Field label="HRB" value={s.contact.register_no} onChange={(v) => setContact("register_no", v)} />
          </div>
          <Field label="USt-IdNr." value={s.contact.vat_id} onChange={(v) => setContact("vat_id", v)} />
        </section>
      </div>
    </div>
  );
}
